// hooks/useImageUpload.js
import { useState, useCallback, useRef } from 'react';
import { imageApi } from '../utils/api/ImageApi';

/**
 * Custom hook for handling image uploads with drag-and-drop functionality
 */
export const useImageUpload = ({
  onImageInserted,
  currentDocument = null,
  getClassContext = null,
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);
  const [isValidFile, setIsValidFile] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle', 'uploading', 'success', 'error'
  const [uploadMessage, setUploadMessage] = useState('');
  const [currentFilename, setCurrentFilename] = useState('');
  
  const dragCounterRef = useRef(0);

  /**
   * Check if the dragged item contains image files
   */
  const hasImageFiles = useCallback((dataTransfer) => {
    if (!dataTransfer) return false;
    
    // Check if dragging files
    if (dataTransfer.files && dataTransfer.files.length > 0) {
      return Array.from(dataTransfer.files).some(file => 
        file.type.startsWith('image/')
      );
    }
    
    // Check if dragging from browser (could be image)
    const types = Array.from(dataTransfer.types || []);
    return types.includes('Files');
  }, []);

  /**
   * Check if dragged content is existing markdown image
   */
  const isExistingImage = useCallback((dataTransfer) => {
    const text = dataTransfer?.getData('text/plain') || '';
    return text.includes('![') && text.includes('](');
  }, []);

  /**
   * Handle drag enter event
   */
  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    dragCounterRef.current++;
    
    if (hasImageFiles(e.dataTransfer)) {
      setIsDragging(true);
      setDragPosition({ x: e.clientX, y: e.clientY });
      
      // Validate file type
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        const validation = imageApi.validateImageFile(file);
        setIsValidFile(validation.valid);
      }
    }
  }, [disabled, hasImageFiles]);

  /**
   * Handle drag over event
   */
  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (isDragging) {
      setDragPosition({ x: e.clientX, y: e.clientY });
    }
  }, [disabled, isDragging]);

  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback((e) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    dragCounterRef.current--;
    
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
      setDragPosition(null);
      setIsValidFile(true);
    }
  }, [disabled]);

  /**
   * Upload image file
   */
  const uploadImage = useCallback(async (file, insertPosition = null) => {
    if (disabled) return;
    
    // Validate file
    const validation = imageApi.validateImageFile(file);
    if (!validation.valid) {
      setUploadStatus('error');
      setUploadMessage(validation.error);
      setTimeout(() => {
        setUploadStatus('idle');
        setUploadMessage('');
      }, 5000);
      return;
    }

    try {
      setUploadStatus('uploading');
      setUploadProgress(0);
      setCurrentFilename(file.name);
      setUploadMessage('');

      const classContext = getClassContext?.();
      const classId = classContext?.class_id || null;

      const result = await imageApi.uploadImage(
        file,
        currentDocument?.id,
        classId,
        (progress) => setUploadProgress(progress)
      );

      if (result.success) {
        setUploadStatus('success');
        setUploadMessage(result.message || 'Upload complete');
        
        // Generate markdown for the image
        const altText = result.file.original_filename.replace(/\.[^/.]+$/, ""); // Remove extension
        const imageMarkdown = `![${altText}](${result.file.url})`;

        // Insert image into document
        if (onImageInserted) {
          onImageInserted(imageMarkdown, insertPosition, {
            deduplicated: result.file.deduplicated || false,
            filename: result.file.filename,
            size: result.file.size || 0
          });
        }

        // Clear status after delay
        setTimeout(() => {
          setUploadStatus('idle');
          setUploadMessage('');
          setCurrentFilename('');
        }, 3000);
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      setUploadStatus('error');
      setUploadMessage(error.message || 'Upload failed');
      
      // Clear error after delay
      setTimeout(() => {
        setUploadStatus('idle');
        setUploadMessage('');
        setCurrentFilename('');
      }, 5000);
    }
  }, [disabled, currentDocument, getClassContext, onImageInserted]);

  /**
   * Handle drop event
   */
  const handleDrop = useCallback((e) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
    setDragPosition(null);
    dragCounterRef.current = 0;

    const dataTransfer = e.dataTransfer;

    // Check if it's an existing image being moved
    if (isExistingImage(dataTransfer)) {
      // This is moving an existing image - no upload needed
      const markdownText = dataTransfer.getData('text/plain');
      if (onImageInserted) {
        onImageInserted(markdownText, { x: e.clientX, y: e.clientY });
      }
      return;
    }

    // Handle new file upload
    if (dataTransfer.files && dataTransfer.files.length > 0) {
      const file = dataTransfer.files[0];
      uploadImage(file, { x: e.clientX, y: e.clientY });
    }
  }, [disabled, isExistingImage, onImageInserted, uploadImage]);

  /**
   * Reset upload state
   */
  const resetUpload = useCallback(() => {
    setUploadStatus('idle');
    setUploadProgress(0);
    setUploadMessage('');
    setCurrentFilename('');
  }, []);

  return {
    // Drag and drop state
    isDragging,
    dragPosition,
    isValidFile,
    
    // Upload state
    uploadStatus,
    uploadProgress,
    uploadMessage,
    currentFilename,
    
    // Drag and drop handlers
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    
    // Upload functions
    uploadImage,
    resetUpload,
    
    // Utility functions
    hasImageFiles,
    isExistingImage
  };
};